import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Calendar, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Plus, Eye, Trash2, Edit, CalendarDays, CalendarRange, Layers,
  User, FileText, AlertCircle, Upload, Download, X, Send, Users,
  ArrowUp, ArrowDown, ArrowUpDown, Archive, RotateCcw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkTasks = () => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('daily');
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [unreadTaskIds, setUnreadTaskIds] = useState(new Set()); // Okunmamış bildirim olan görevler
  const [unreadByType, setUnreadByType] = useState({ daily: 0, monthly: 0 }); // Tab başına okunmamış sayısı
  
  // Sorting state - varsayılan: son güncelleme (en yeni üstte)
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [newProgressLog, setNewProgressLog] = useState({ description: '', documents: [] });
  
  // Completion modal state
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionData, setCompletionData] = useState({
    completion_notes: '',
    completion_date: new Date().toISOString().split('T')[0]
  });
  
  // Finish notification modal state (atanan kişi için)
  const [finishNotifyModalOpen, setFinishNotifyModalOpen] = useState(false);
  const [finishNotifyNotes, setFinishNotifyNotes] = useState('');
  
  // Form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    task_type: 'daily',
    start_date: '',
    end_date: '',
    priority: 'normal',
    documents: [],
    send_to_users: []
  });

  useEffect(() => {
    if (mainTab === 'archive') {
      loadArchivedTasks();
    } else {
      loadTasks();
    }
    loadUsers();
    loadUnreadNotifications(); // Okunmamış bildirimleri yükle
  }, [mainTab]);

  // Okunmamış bildirimleri yükle - hangi görevlerin güncelleme aldığını bul
  const loadUnreadNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'gorevler', is_read: false },
        headers: getAuthHeaders()
      });
      const notifications = res.data || [];
      // related_id'leri topla
      const ids = new Set(notifications.map(n => n.related_id).filter(Boolean));
      setUnreadTaskIds(ids);
      
      // Tab başına okunmamış sayısını hesapla
      // Görev tiplerini almak için tasks'a bakacağız
      const taskRes = await axios.get(`${API}/work/gorevler`, { headers: getAuthHeaders() });
      const allTasks = taskRes.data || [];
      
      let dailyCount = 0;
      let monthlyCount = 0;
      
      allTasks.forEach(task => {
        if (ids.has(task.id)) {
          if (task.task_type === 'daily') dailyCount++;
          else if (task.task_type === 'monthly') monthlyCount++;
        }
      });
      
      setUnreadByType({ daily: dailyCount, monthly: monthlyCount });
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    }
  };

  // Tab değiştiğinde o tab'daki görevlerin bildirimlerini okundu yap
  const handleTabChange = async (newTab) => {
    setMainTab(newTab);
    
    // Arşiv tab'ına geçişte bildirim işleme yapma
    if (newTab === 'archive') return;
    
    // Bu tab'daki okunmamış görevleri bul ve okundu yap
    try {
      const tasksInTab = tasks.filter(t => t.task_type === newTab);
      const unreadInTab = tasksInTab.filter(t => unreadTaskIds.has(t.id));
      
      for (const task of unreadInTab) {
        await markNotificationAsRead(task.id);
      }
      
      // Badge sayısını güncelle
      setUnreadByType(prev => ({
        ...prev,
        [newTab]: 0
      }));
      
      // Menü badge'ini de güncelle - tüm gorevler kategorisindeki bildirimleri yeniden yükle
      loadUnreadNotifications();
    } catch (error) {
      console.error('Tab bildirimleri okundu yapılırken hata:', error);
    }
  };

  // Sayfa ilk yüklendiğinde mevcut tab'daki bildirimleri okunmuş yap
  useEffect(() => {
    // Görevler sayfasına girildiğinde - sadece görevlerin olduğu bildirimleri işle
    const markCurrentTabAsRead = async () => {
      if (tasks.length > 0 && unreadTaskIds.size > 0) {
        const tasksInTab = tasks.filter(t => t.task_type === mainTab);
        const unreadInTab = tasksInTab.filter(t => unreadTaskIds.has(t.id));
        
        for (const task of unreadInTab) {
          await markNotificationAsRead(task.id);
        }
        
        setUnreadByType(prev => ({
          ...prev,
          [mainTab]: 0
        }));
      }
    };
    
    if (tasks.length > 0) {
      markCurrentTabAsRead();
    }
  }, [tasks, mainTab]);

  // Bildirimi okundu olarak işaretle
  const markNotificationAsRead = async (taskId) => {
    try {
      // Bu görevle ilgili tüm bildirimleri okundu yap
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'gorevler', is_read: false },
        headers: getAuthHeaders()
      });
      const notifications = res.data || [];
      const relatedNotifs = notifications.filter(n => n.related_id === taskId);
      
      for (const notif of relatedNotifs) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      
      // State'i güncelle
      setUnreadTaskIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    } catch (error) {
      console.error('Bildirim okundu yapılırken hata:', error);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/gorevler`, {
        params: { task_type: mainTab },
        headers: getAuthHeaders()
      });
      setTasks(res.data || []);
    } catch (error) {
      console.error('Görevler yüklenirken hata:', error);
      toast.error('Görevler yüklenemedi');
    }
    setLoading(false);
  };

  const loadArchivedTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/gorevler/archived`, {
        headers: getAuthHeaders()
      });
      setArchivedTasks(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş görevler yüklenirken hata:', error);
      toast.error('Arşivlenmiş görevler yüklenemedi');
    }
    setLoading(false);
  };

  const restoreTask = async (taskId) => {
    try {
      await axios.post(`${API}/work/gorevler/${taskId}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('Görev arşivden geri yüklendi');
      loadArchivedTasks();
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      toast.error('Görev geri yüklenemedi');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/calendar/users-for-share`, { headers: getAuthHeaders() });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // Document upload handler
  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewTask(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (index) => {
    setNewTask(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const toggleUserSelection = (userId) => {
    setNewTask(prev => {
      const users = prev.send_to_users.includes(userId)
        ? prev.send_to_users.filter(id => id !== userId)
        : [...prev.send_to_users, userId];
      return { ...prev, send_to_users: users };
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isPreviewable = (type) => {
    return type && (type.startsWith('image/') || type === 'application/pdf');
  };

  const handleCreateTask = async () => {
    if (!newTask.title) {
      toast.error('Görev başlığı gerekli');
      return;
    }
    
    try {
      await axios.post(`${API}/work/gorevler`, {
        ...newTask,
        task_type: mainTab,
        owner_name: user?.name || 'Bilinmiyor'
      }, { headers: getAuthHeaders() });
      toast.success('Görev oluşturuldu');
      setCreateModalOpen(false);
      setNewTask({
        title: '',
        description: '',
        task_type: mainTab,
        start_date: '',
        end_date: '',
        priority: 'normal',
        documents: [],
        send_to_users: []
      });
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev oluşturulamadı');
    }
  };

  const handleUpdateTask = async () => {
    if (!selectedTask) return;
    
    try {
      await axios.put(`${API}/work/gorevler/${selectedTask.id}`, {
        title: selectedTask.title,
        description: selectedTask.description,
        start_date: selectedTask.start_date,
        end_date: selectedTask.end_date,
        priority: selectedTask.priority,
        status: selectedTask.status,
        send_to_users: selectedTask.send_to_users || []
      }, { headers: getAuthHeaders() });
      toast.success('Görev güncellendi');
      setEditModalOpen(false);
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev güncellenemedi');
    }
  };

  // Düzenleme modalında kişi seçimi toggle
  const toggleEditUserSelection = (userId) => {
    if (!selectedTask) return;
    const currentUsers = selectedTask.send_to_users || [];
    const newUsers = currentUsers.includes(userId)
      ? currentUsers.filter(id => id !== userId)
      : [...currentUsers, userId];
    setSelectedTask({ ...selectedTask, send_to_users: newUsers });
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/work/gorevler/${taskId}`, { headers: getAuthHeaders() });
      toast.success('Görev silindi');
      loadTasks();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev silinemedi');
    }
  };

  const handleAcceptTask = async (taskId) => {
    try {
      await axios.post(`${API}/work/gorevler/${taskId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success('Görev kabul edildi');
      loadTasks();
    } catch (error) {
      toast.error('Görev kabul edilemedi');
    }
  };

  const loadProgressLogs = async (taskId) => {
    try {
      const res = await axios.get(`${API}/work/gorevler/${taskId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
    }
  };

  const handleOpenProgressModal = (task) => {
    setSelectedTask(task);
    loadProgressLogs(task.id);
    setProgressModalOpen(true);
  };

  const handleProgressDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewProgressLog(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAddProgressLog = async () => {
    if (!newProgressLog.description.trim()) {
      toast.error('Açıklama gerekli');
      return;
    }
    try {
      await axios.post(`${API}/work/gorevler/${selectedTask.id}/progress`, newProgressLog, { headers: getAuthHeaders() });
      toast.success('Güncel durum eklendi');
      setNewProgressLog({ description: '', documents: [] });
      loadProgressLogs(selectedTask.id);
      loadTasks();
    } catch (error) {
      toast.error('Güncel durum eklenemedi');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/work/gorevler/${taskId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success('Durum güncellendi');
      loadTasks();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleOpenCompletionModal = (task) => {
    setSelectedTask(task);
    setCompletionData({
      completion_notes: '',
      completion_date: new Date().toISOString().split('T')[0]
    });
    setDetailModalOpen(false);
    setCompletionModalOpen(true);
  };

  // Görevi Bitir bildirimi modalını aç (atanan kişi için)
  const handleOpenFinishNotifyModal = (task) => {
    setSelectedTask(task);
    setFinishNotifyNotes('');
    setDetailModalOpen(false);
    setFinishNotifyModalOpen(true);
  };

  // Görevi bitirdiğini bildir (durumu değiştirmez)
  const handleFinishNotify = async () => {
    if (!selectedTask) return;
    
    if (!finishNotifyNotes.trim()) {
      toast.error('Bitirme açıklaması gerekli');
      return;
    }
    
    try {
      await axios.post(`${API}/work/gorevler/${selectedTask.id}/finish-notify`, {
        notes: finishNotifyNotes
      }, { headers: getAuthHeaders() });
      toast.success('Görev bitirme bildirimi gönderildi');
      setFinishNotifyModalOpen(false);
      loadTasks();
    } catch (error) {
      toast.error('Bildirim gönderilemedi');
    }
  };

  const handleCompleteWithDetails = async () => {
    if (!selectedTask) return;
    
    if (!completionData.completion_notes.trim()) {
      toast.error('Tamamlama açıklaması gerekli');
      return;
    }
    
    try {
      // Tamamlanma tarihi ve saati otomatik olarak şimdiki zaman
      const completionDateTime = new Date().toISOString();
      
      await axios.put(`${API}/work/gorevler/${selectedTask.id}`, {
        status: 'completed',
        completion_notes: completionData.completion_notes,
        completion_date: completionDateTime,
        completed_by: user?.id,
        completed_by_name: user?.name
      }, { headers: getAuthHeaders() });
      toast.success('Görev tamamlandı');
      setCompletionModalOpen(false);
      loadTasks();
    } catch (error) {
      toast.error('Görev tamamlanamadı');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      planned: { label: 'Planlandı', color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_progress: { label: 'Devam Ediyor', color: 'bg-amber-100 text-amber-700', icon: RefreshCw },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config.planned;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Düşük', color: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', color: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getTabIcon = (type) => {
    switch(type) {
      case 'daily': return CalendarDays;
      case 'monthly': return CalendarRange;
      case 'master': return Layers;
      case 'archive': return Archive;
      default: return Calendar;
    }
  };

  const getTabLabel = (type) => {
    switch(type) {
      case 'daily': return 'GÜNLÜK GÖREV';
      case 'monthly': return 'AYLIK GÖREV';
      case 'archive': return 'ARŞİV';
      case 'master': return 'MASTER GÖREV';
      default: return type;
    }
  };

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const sortedTasks = React.useMemo(() => {
    if (!sortConfig.key) return tasks;
    
    return [...tasks].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'title':
          aVal = a.title?.toLowerCase() || '';
          bVal = b.title?.toLowerCase() || '';
          break;
        case 'owner':
          aVal = (a.owner?.name || a.owner_name || '').toLowerCase();
          bVal = (b.owner?.name || b.owner_name || '').toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'accepted_by':
          aVal = a.accepted_by?.length || 0;
          bVal = b.accepted_by?.length || 0;
          break;
        case 'date_range':
          aVal = new Date(a.start_date || 0).getTime();
          bVal = new Date(b.start_date || 0).getTime();
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { completed: 4, in_progress: 3, planned: 2, cancelled: 1 };
          aVal = statusOrder[a.status] || 0;
          bVal = statusOrder[b.status] || 0;
          break;
        case 'completion_date':
          aVal = a.completion_date ? new Date(a.completion_date).getTime() : 0;
          bVal = b.completion_date ? new Date(b.completion_date).getTime() : 0;
          break;
        case 'updated_at':
          aVal = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bVal = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tasks, sortConfig]);

  const renderTaskTable = () => {
    if (tasks.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Görev Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride görev yok</p>
            <Button 
              onClick={() => setCreateModalOpen(true)}
              className="mt-4 gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
            >
              <Plus className="h-4 w-4" />
              Görev Ekle
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('title')}
                  data-testid="sort-title"
                >
                  <div className="flex items-center gap-2">
                    Görev
                    {getSortIcon('title')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('owner')}
                  data-testid="sort-owner"
                >
                  <div className="flex items-center gap-2">
                    Oluşturan
                    {getSortIcon('owner')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_at')}
                  data-testid="sort-created"
                >
                  <div className="flex items-center gap-2">
                    Oluşturulma
                    {getSortIcon('created_at')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('accepted_by')}
                  data-testid="sort-accepted"
                >
                  <div className="flex items-center gap-2">
                    Kabul Edenler
                    {getSortIcon('accepted_by')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('date_range')}
                  data-testid="sort-date-range"
                >
                  <div className="flex items-center gap-2">
                    Tarih Aralığı
                    {getSortIcon('date_range')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('priority')}
                  data-testid="sort-priority"
                >
                  <div className="flex items-center gap-2">
                    Öncelik
                    {getSortIcon('priority')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('status')}
                  data-testid="sort-status"
                >
                  <div className="flex items-center gap-2">
                    Durum
                    {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('updated_at')}
                  data-testid="sort-updated"
                >
                  <div className="flex items-center gap-2">
                    Son Güncelleme
                    {getSortIcon('updated_at')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('completion_date')}
                  data-testid="sort-completion"
                >
                  <div className="flex items-center gap-2">
                    Tamamlanma Zamanı
                    {getSortIcon('completion_date')}
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTasks.map(task => (
                <TableRow key={task.id} data-testid={`task-row-${task.id}`} className={unreadTaskIds.has(task.id) ? 'bg-blue-50' : ''}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="font-medium">{task.title}</p>
                        {task.description && (
                          <p className="text-sm text-gray-500 truncate max-w-[200px]">{task.description}</p>
                        )}
                      </div>
                      {unreadTaskIds.has(task.id) && (
                        <Badge className="bg-blue-500 text-white text-xs animate-pulse">YENİ</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {task.owner?.name || task.owner_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-600">
                      {task.created_at 
                        ? new Date(task.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {task.accepted_by && task.accepted_by.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {task.accepted_by.map((a, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700">
                            {a.user_name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.start_date ? (
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {new Date(task.start_date).toLocaleDateString('tr-TR')}
                        </div>
                        {task.end_date && (
                          <div className="text-gray-500">
                            → {new Date(task.end_date).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {getStatusBadge(task.status)}
                      {task.finish_notifications && task.finish_notifications.length > 0 && task.status !== 'completed' && (
                        <Badge className="bg-amber-100 text-amber-700 gap-1 text-xs">
                          <Send className="h-3 w-3" />
                          Görev Bitmiş
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {task.updated_at ? (
                      <div className="text-sm text-blue-600 font-medium">
                        {new Date(task.updated_at).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.status === 'completed' && task.completion_date ? (
                      <div className="text-sm text-green-600">
                        {new Date(task.completion_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { 
                          setSelectedTask(task); 
                          setDetailModalOpen(true); 
                          if (unreadTaskIds.has(task.id)) {
                            markNotificationAsRead(task.id);
                          }
                        }}
                        data-testid={`view-task-${task.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {task.owner_id === user?.id && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedTask({...task}); setEditModalOpen(true); }}
                            data-testid={`edit-task-${task.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteTask(task.id)}
                            data-testid={`delete-task-${task.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="work-tasks">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GÖREVLER</h1>
          <p className="text-gray-500">Günlük ve aylık görevlerinizi yönetin</p>
        </div>
        <Button onClick={loadTasks} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-600))]" />
        </div>
      ) : (
        <Tabs value={mainTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            {['daily', 'monthly', 'archive'].map(type => {
              const Icon = getTabIcon(type);
              const unreadCount = type === 'archive' ? 0 : unreadByType[type] || 0;
              return (
                <TabsTrigger 
                  key={type} 
                  value={type} 
                  className="gap-2 relative"
                  data-testid={`tab-${type}`}
                >
                  <Icon className="h-4 w-4" />
                  {getTabLabel(type)}
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center animate-pulse">
                      {unreadCount}
                    </Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* GÜNLÜK GÖREV */}
          <TabsContent value="daily" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                data-testid="add-daily-task-btn"
              >
                <Plus className="h-4 w-4" />
                GÖREV EKLE
              </Button>
            </div>
            {renderTaskTable()}
          </TabsContent>

          {/* AYLIK GÖREV */}
          <TabsContent value="monthly" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                data-testid="add-monthly-task-btn"
              >
                <Plus className="h-4 w-4" />
                GÖREV EKLE
              </Button>
            </div>
            {renderTaskTable()}
          </TabsContent>

          {/* ARŞİV */}
          <TabsContent value="archive" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-gray-500" />
                  Arşivlenmiş Görevler
                </CardTitle>
              </CardHeader>
              <CardContent>
                {archivedTasks.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Archive className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Arşivde görev bulunmuyor</p>
                    <p className="text-sm">Silinen görevler burada görüntülenecek</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Başlık</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead>Arşivlenme Tarihi</TableHead>
                        <TableHead>Arşivleyen</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedTasks.map(task => (
                        <TableRow key={task.id}>
                          <TableCell className="font-medium">{task.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {task.task_type === 'daily' ? 'Günlük' : task.task_type === 'monthly' ? 'Aylık' : task.task_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {task.archived_at ? new Date(task.archived_at).toLocaleDateString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell>{task.archived_by_name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedTask(task);
                                  setDetailModalOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => restoreTask(task.id)}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>


        </Tabs>
      )}

      {/* Görev Oluşturma Modalı */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Yeni Görev Ekle - {getTabLabel(mainTab)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="task-title">Görev Başlığı *</Label>
              <Input 
                id="task-title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Görev başlığını girin"
                data-testid="task-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="task-desc">Açıklama</Label>
              <Textarea 
                id="task-desc"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Görev açıklaması"
                rows={3}
                data-testid="task-desc-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="task-start">Başlangıç Tarihi</Label>
                <Input 
                  id="task-start"
                  type="date"
                  value={newTask.start_date}
                  onChange={(e) => setNewTask({ ...newTask, start_date: e.target.value })}
                  data-testid="task-start-input"
                />
              </div>
              
              <div>
                <Label htmlFor="task-end">Bitiş Tarihi</Label>
                <Input 
                  id="task-end"
                  type="date"
                  value={newTask.end_date}
                  onChange={(e) => setNewTask({ ...newTask, end_date: e.target.value })}
                  data-testid="task-end-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="task-priority">Öncelik</Label>
              <Select 
                value={newTask.priority} 
                onValueChange={(val) => setNewTask({ ...newTask, priority: val })}
              >
                <SelectTrigger data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Belge Yükleme */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-blue-500" />
                  Belgeler
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <span className="text-sm text-blue-600 hover:underline">+ Belge Ekle</span>
                </label>
              </div>
              
              {newTask.documents.length > 0 && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {newTask.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        onClick={() => removeDocument(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kişilere Gönder */}
            <div className="border rounded-lg p-3 space-y-3">
              <Label className="flex items-center gap-2">
                <Send className="h-4 w-4 text-green-500" />
                Kişilere Gönder (Opsiyonel)
              </Label>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                        newTask.send_to_users.includes(u.id) ? 'bg-green-100' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{u.name}</span>
                      </div>
                      {newTask.send_to_users.includes(u.id) && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">Paylaşılacak kullanıcı yok</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateTask}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="submit-task-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Görev Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Görev Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-700))]">
                    {getTabLabel(selectedTask.task_type)}
                  </Badge>
                  {getStatusBadge(selectedTask.status)}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedTask.title}</h3>
                {selectedTask.description && (
                  <p className="text-gray-600">{selectedTask.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{selectedTask.owner?.name || selectedTask.owner_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedTask.created_at 
                      ? new Date(selectedTask.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Öncelik</Label>
                  <div className="mt-1">{getPriorityBadge(selectedTask.priority)}</div>
                </div>
                {selectedTask.start_date && (
                  <div>
                    <Label className="text-gray-500">Başlangıç</Label>
                    <p className="font-medium">{new Date(selectedTask.start_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
                {selectedTask.end_date && (
                  <div>
                    <Label className="text-gray-500">Bitiş</Label>
                    <p className="font-medium">{new Date(selectedTask.end_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedTask.documents && selectedTask.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Ekli Belgeler</Label>
                  <div className="space-y-2">
                    {selectedTask.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = doc.data;
                              link.download = doc.name;
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gönderilen Kişiler */}
              {selectedTask.send_to_users && selectedTask.send_to_users.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Gönderilen Kişiler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.send_to_users.map((userId, idx) => {
                      const u = users.find(us => us.id === userId);
                      return (
                        <Badge key={idx} variant="outline" className="gap-1">
                          <User className="h-3 w-3" />
                          {u?.name || userId}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Kabul Edenler */}
              {selectedTask.accepted_by && selectedTask.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        {a.user_name} - {new Date(a.accepted_at).toLocaleDateString('tr-TR')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Bitirme Bildirimleri */}
              {selectedTask.finish_notifications && selectedTask.finish_notifications.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block flex items-center gap-2">
                    <Send className="h-4 w-4 text-amber-600" />
                    Bitirme Bildirimleri
                  </Label>
                  <div className="space-y-2">
                    {selectedTask.finish_notifications.map((notification, idx) => (
                      <div key={idx} className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-amber-800">{notification.user_name}</span>
                          <span className="text-xs text-amber-600">
                            {new Date(notification.notified_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{notification.notes}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanmış/İptal edilmiş için sadece Güncel Durum */}
              {(selectedTask.status === 'completed' || selectedTask.status === 'cancelled') && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Geçmiş Kayıtlar</Label>
                  <Button 
                    onClick={() => {
                      setDetailModalOpen(false);
                      handleOpenProgressModal(selectedTask);
                    }}
                    variant="outline"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Güncel Durum Geçmişi
                  </Button>
                </div>
              )}

              {/* Durum Değiştirme - Aktif görevler için */}
              {selectedTask.status !== 'completed' && selectedTask.status !== 'cancelled' && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">İşlemler</Label>
                  <div className="flex gap-2 flex-wrap">
                    {/* Başlat - Herkes basabilir */}
                    {selectedTask.status === 'planned' && (
                      <Button 
                        onClick={() => {
                          handleAcceptTask(selectedTask.id);
                          setDetailModalOpen(false);
                        }}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Başlat
                      </Button>
                    )}
                    
                    {/* Güncel Durum - Herkes görebilir */}
                    <Button 
                      onClick={() => {
                        setDetailModalOpen(false);
                        handleOpenProgressModal(selectedTask);
                      }}
                      variant="outline"
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Güncel Durum
                    </Button>
                    
                    {/* Görevi Bitir - Atanan kişiler (send_to_users içinde olan herkes) - Sadece bildirim gönderir */}
                    {selectedTask.status === 'in_progress' && 
                     selectedTask.send_to_users && 
                     selectedTask.send_to_users.includes(user?.id) &&
                     selectedTask.owner_id !== user?.id && (
                      <Button 
                        onClick={() => handleOpenFinishNotifyModal(selectedTask)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="finish-notify-btn"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Görevi Bitir
                      </Button>
                    )}
                    
                    {/* Tamamla ve İptal - Sadece oluşturan kişi */}
                    {selectedTask.owner_id === user?.id && selectedTask.status === 'in_progress' && (
                      <>
                        <Button 
                          onClick={() => handleOpenCompletionModal(selectedTask)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Tamamla
                        </Button>
                        <Button 
                          onClick={() => handleStatusChange(selectedTask.id, 'cancelled')}
                          variant="destructive"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          İptal Et
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Görev Düzenleme Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Görev Düzenle
            </DialogTitle>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Görev Başlığı</Label>
                <Input 
                  id="edit-title"
                  value={selectedTask.title}
                  onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                  data-testid="edit-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-desc">Açıklama</Label>
                <Textarea 
                  id="edit-desc"
                  value={selectedTask.description || ''}
                  onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                  rows={3}
                  data-testid="edit-desc-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-start">Başlangıç Tarihi</Label>
                  <Input 
                    id="edit-start"
                    type="date"
                    value={selectedTask.start_date || ''}
                    onChange={(e) => setSelectedTask({ ...selectedTask, start_date: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-end">Bitiş Tarihi</Label>
                  <Input 
                    id="edit-end"
                    type="date"
                    value={selectedTask.end_date || ''}
                    onChange={(e) => setSelectedTask({ ...selectedTask, end_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Öncelik</Label>
                <Select 
                  value={selectedTask.priority} 
                  onValueChange={(val) => setSelectedTask({ ...selectedTask, priority: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Kişilere Gönder - Düzenleme */}
              <div className="border rounded-lg p-3 space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-500" />
                  Atanan Kişiler ({(selectedTask.send_to_users || []).length} seçili)
                </Label>
                <ScrollArea className="h-32">
                  <div className="space-y-1">
                    {users.map(u => {
                      const isSelected = (selectedTask.send_to_users || []).includes(u.id);
                      return (
                        <div
                          key={u.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                            isSelected ? 'bg-green-100' : 'hover:bg-gray-100'
                          }`}
                          onClick={() => toggleEditUserSelection(u.id)}
                        >
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{u.name}</span>
                          </div>
                          {isSelected && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      );
                    })}
                    {users.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-2">Kullanıcı bulunamadı</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdateTask}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="save-task-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewDoc?.data;
                link.download = previewDoc?.name;
                link.click();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedTask?.title}
            </DialogTitle>
          </DialogHeader>
          
          {/* Yeni Güncelleme Ekle - Sadece aktif görevler için */}
          {selectedTask?.status !== 'completed' && selectedTask?.status !== 'cancelled' && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-5 w-5 text-green-600" />
                <Label className="font-semibold">Yeni Güncelleme Ekle</Label>
              </div>
              <Textarea
                value={newProgressLog.description}
                onChange={(e) => setNewProgressLog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ne yapıldı? Açıklama yazın..."
                rows={3}
              />
              <div className="flex items-center justify-between">
                <label className="cursor-pointer flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleProgressDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <Upload className="h-4 w-4" />
                  Belge/Fotoğraf Ekle
                </label>
                <Button onClick={handleAddProgressLog} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ekle
                </Button>
              </div>
              {newProgressLog.documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newProgressLog.documents.map((doc, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {doc.name}
                      <button
                        onClick={() => setNewProgressLog(prev => ({
                          ...prev,
                          documents: prev.documents.filter((_, i) => i !== idx)
                        }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Tamamlanmış/İptal edilmiş için bilgi mesajı */}
          {(selectedTask?.status === 'completed' || selectedTask?.status === 'cancelled') && (
            <div className="border rounded-lg p-4 bg-blue-50 text-blue-700">
              <p className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Bu görev {selectedTask?.status === 'completed' ? 'tamamlanmış' : 'iptal edilmiş'}. Sadece geçmiş kayıtları görüntüleyebilirsiniz.
              </p>
            </div>
          )}

          {/* Güncellemeler Listesi */}
          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-500 hover:text-blue-700 ml-1"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name;
                                link.click();
                              }}
                              className="text-green-500 hover:text-green-700 ml-1"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tamamlama Modal */}
      <Dialog open={completionModalOpen} onOpenChange={setCompletionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Görevi Tamamla
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedTask?.title}</p>
              <p className="text-sm text-gray-500">{selectedTask?.description}</p>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-blue-700 text-sm">
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Tamamlanma tarihi ve saati otomatik olarak kaydedilecektir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="completion-notes">Tamamlama Açıklaması *</Label>
              <Textarea 
                id="completion-notes"
                value={completionData.completion_notes}
                onChange={(e) => setCompletionData(prev => ({ ...prev, completion_notes: e.target.value }))}
                placeholder="Görevin nasıl tamamlandığını açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletionModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCompleteWithDetails}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görevi Bitir Bildirimi Modalı - Atanan kişi için */}
      <Dialog open={finishNotifyModalOpen} onOpenChange={setFinishNotifyModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-amber-600" />
              Görev Bitirme Bildirimi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedTask?.title}</p>
              <p className="text-sm text-gray-500">{selectedTask?.description}</p>
            </div>
            
            <div className="bg-amber-50 p-3 rounded-lg text-amber-700 text-sm">
              <p className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Bu işlem görevi tamamlamaz, sadece görevi veren kişiye bildirim gönderir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="finish-notes">Bitirme Açıklaması *</Label>
              <Textarea 
                id="finish-notes"
                value={finishNotifyNotes}
                onChange={(e) => setFinishNotifyNotes(e.target.value)}
                placeholder="Görevi nasıl tamamladığınızı açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinishNotifyModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleFinishNotify}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Bildirimi Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkTasks;
